import { useNestedQueryParamHelper, useQueryParamHelper } from '@shared/core/queryString';
import { useEffect, useMemo } from 'react';
import { processDestination, processLatLngValue, processDate, processRooms, processSearchRadius } from './paramValidation';

export type SearchParamsValue = {
  destination: ReturnType<typeof processDestination>;
  lat: ReturnType<typeof processLatLngValue>;
  lng: ReturnType<typeof processLatLngValue>;
  startDate: ReturnType<typeof processDate>;
  endDate: ReturnType<typeof processDate>;
  rooms: ReturnType<typeof processRooms>;
};

export type Props = {
  onValueUpdate: (value: SearchParamsValue) => void;
};

type UseStaysSearchParamsArgs = {
  onValueUpdate?: (value: SearchParamsValue) => void;
};

const extractRelevantSearchParams = (helper: ReturnType<typeof useQueryParamHelper>) => {
  // latitude
  const lat = helper.getValueString('lat');
  // longitude
  const lng = helper.getValueString('lng');
  // display string of location
  const destination = helper.getValueString('destination');
  // dates - start
  const startDate = helper.getValueString('checkin') || helper.getValueString('startDate');
  // dates - end
  const endDate = helper.getValueString('checkout') || helper.getValueString('endDate');

  const searchRadius = helper.getValueInteger('radius');

  /**
   * rooms are a special domain language rooms are comma separated occupants
   * e.g. a1:c6:c5,a2:c3
   * This means 2 rooms:
   * - 1st room has 1 adult and 2 children aged 6 and 5
   * - 2nd room has 2 adults and 1 child aged 3
   */
  const rooms = helper.getValueString('rooms');

  return {
    destination: processDestination(destination),
    lat: processLatLngValue(lat),
    lng: processLatLngValue(lng),
    searchRadius: processSearchRadius(searchRadius),
    startDate: processDate(startDate),
    endDate: processDate(endDate),
    rooms: processRooms(rooms),
  };
};

const useStaysSearchParams = ({ onValueUpdate }: UseStaysSearchParamsArgs = {}) => {
  const helper = useQueryParamHelper();
  const value = useMemo(() => {
    return extractRelevantSearchParams(helper);
  }, [helper]);
  useEffect(() => {
    onValueUpdate?.(value);
  }, [value, onValueUpdate]);

  return value;
};

const useNestedStaysSearchParams = ({ onValueUpdate, rootQueryParamKey }: UseStaysSearchParamsArgs & { rootQueryParamKey: string }) => {
  const helper = useNestedQueryParamHelper(rootQueryParamKey);

  const value = useMemo(() => {
    return extractRelevantSearchParams(helper);
  }, [helper]);
  useEffect(() => {
    onValueUpdate?.(value);
  }, [value, onValueUpdate]);

  return value;
};

const SearchParamsObserver = ({ onValueUpdate }: Props) => {
  useStaysSearchParams({ onValueUpdate });
  return null;
};

export { useStaysSearchParams, SearchParamsObserver, useNestedStaysSearchParams };
